import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Minus, Plus, X, Icon } from '../../ui/Icon';
import { legDayRanges } from '../../../utils/tripLegs';

export interface NightsLeg { key: string; name: string; nights: number }

interface Props {
  legs: NightsLeg[];
  /** The trip's nights: what the legs have to add up to. */
  total: number;
  onNights: (key: string, nights: number) => void;
  /** Only where the order and the list itself can change (the trip's settings). */
  onMove?: (key: string, delta: -1 | 1) => void;
  onRemove?: (key: string) => void;
}

/** ADR-0030: nights per destination; the days each one covers are derived. */
export default function LegNightsList({ legs, total, onNights, onMove, onRemove }: Props) {
  const { t } = useTranslation();
  const k = 'dashboard.newTripWizard';
  const assigned = legs.reduce((a, l) => a + l.nights, 0);
  const ranges = legDayRanges(legs.map(l => l.nights));

  return (
    <div className="space-y-2">
      <ol className="space-y-2">
        {legs.map((leg, i) => {
          const r = ranges[i];
          return (
            <li key={leg.key} className="flex items-center gap-3 p-3 rounded-2xl border border-slate-200 dark:border-slate-700">
              {onMove && (
                <div className="flex flex-col">
                  <button type="button" disabled={i === 0} onClick={() => onMove(leg.key, -1)}
                    className="btn-icon w-6 h-6 disabled:opacity-30" aria-label={t('trip.destinations.moveUp', { name: leg.name })}>
                    <Icon as={ChevronUp} size={14} />
                  </button>
                  <button type="button" disabled={i === legs.length - 1} onClick={() => onMove(leg.key, 1)}
                    className="btn-icon w-6 h-6 disabled:opacity-30" aria-label={t('trip.destinations.moveDown', { name: leg.name })}>
                    <Icon as={ChevronDown} size={14} />
                  </button>
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-[15px] font-medium text-slate-900 dark:text-slate-100 truncate">{leg.name}</p>
                <p className="text-[12px] text-slate-400 dark:text-slate-500">
                  {r.to > r.from ? t(`${k}.legDays`, { from: r.from, to: r.to }) : t(`${k}.legDay`, { day: r.from })}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button type="button" disabled={leg.nights <= 0} onClick={() => onNights(leg.key, leg.nights - 1)}
                  className="btn-icon w-9 h-9 border border-slate-200 dark:border-slate-700 disabled:opacity-40"
                  aria-label={t(`${k}.legNightsFewer`, { name: leg.name })}>
                  <Icon as={Minus} size={16} />
                </button>
                <span className="w-16 text-center text-[14px] font-medium tabular-nums">{t(`${k}.legNights`, { count: leg.nights })}</span>
                <button type="button" onClick={() => onNights(leg.key, leg.nights + 1)}
                  className="btn-icon w-9 h-9 border border-slate-200 dark:border-slate-700"
                  aria-label={t(`${k}.legNightsMore`, { name: leg.name })}>
                  <Icon as={Plus} size={16} />
                </button>
              </div>
              {onRemove && legs.length > 1 && (
                <button type="button" onClick={() => onRemove(leg.key)}
                  className="btn-icon w-8 h-8 text-slate-400 hover:text-red-500"
                  aria-label={t(`${k}.destinationRemove`, { name: leg.name })}>
                  <Icon as={X} size={14} />
                </button>
              )}
            </li>
          );
        })}
      </ol>
      <p className={`text-[13px] font-medium ${assigned === total ? 'text-teal-600 dark:text-teal-400' : 'text-amber-600 dark:text-amber-400'}`}>
        {t(`${k}.legsTotal`, { assigned, count: total })}
      </p>
    </div>
  );
}
